import { type RefObject, useEffect, useRef } from "react";

import type { BoardRenderer } from "src/client/ai/render/BoardRenderer";
import type { CarrySpec } from "src/api";

/** Total pointer travel, in pixels, that turns a press into a carry. */
export const CARRY_THRESHOLD_PX = 6;

/**
 * The pointer's half of a carry, from wherever it began.
 *
 * **On `window`, because a carry starts in two places and ends in one.** A
 * piece is picked up on the canvas and a tile on the hotbar, which is DOM and
 * has never heard of the renderer; both set `carry`, and from there this is
 * the only thing following the pointer. A captured pointer's events still
 * bubble here, so the canvas' own capture costs nothing.
 *
 * **A drop counts only over the board itself.** The canvas runs under all the
 * chrome, so a square is "under" the pointer even over the hotbar — and
 * letting go on the tile you picked up is how a carry is put back.
 */
export function useCarryGesture(
  renderer: BoardRenderer | undefined,
  canvas: RefObject<HTMLCanvasElement | null>,
  carrying: boolean,
  carry: { spec: CarrySpec; at: { x: number; y: number } } | undefined,
  onDrop: (square: number) => void
): void {
  // Read through refs: the listeners live for the whole carry, and `onDrop`
  // is a new function on every render of the shell.
  const drop = useRef(onDrop);
  drop.current = onDrop;
  /** Where the pointer is now, so a spec that changes mid-carry re-begins there. */
  const point = useRef<{ x: number; y: number } | null>(null);
  /** Did the release land on a square — read by the body's cleanup. */
  const dropped = useRef(false);

  const spec = carry?.spec;
  /** Where the carry began. Only the first frame reads it, so never a dependency. */
  const start = useRef(carry?.at);
  start.current = carry?.at;

  useEffect(() => {
    const from = point.current ?? start.current;
    if (!renderer || !spec || !from) return;
    dropped.current = false;
    renderer.beginCarry(spec, from.x, from.y);
    return () => renderer.endCarry(dropped.current);
  }, [renderer, spec]);

  useEffect(() => {
    if (!carrying) return;
    const overBoard = (x: number, y: number) =>
      document.elementFromPoint(x, y) === canvas.current;
    const move = (event: PointerEvent) => {
      const { clientX: x, clientY: y } = event;
      point.current = { x, y };
      // Over the chrome there is no square to promise, so the body floats.
      renderer?.moveCarry(x, y, overBoard(x, y));
    };
    const up = (event: PointerEvent) => {
      const square =
        renderer && overBoard(event.clientX, event.clientY)
          ? renderer.carrySquare(event.clientX, event.clientY)
          : -1;
      dropped.current = square >= 0;
      drop.current(square);
    };
    const cancel = () => drop.current(-1);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
    return () => {
      point.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
    };
  }, [renderer, canvas, carrying]);
}
